import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import PropTypes from 'prop-types';

 
import { Sparklines, SparklinesLine, SparklinesReferenceLine } from 'react-sparklines';

import { H4, H5 } from '../base';

import { colors, leading, animate } from '../styles/theme';

const cardGutter = '10px';
const cardInnerPadding = '24px';

const Unit = styled('div')`
  font-size: 24px;
  line-height: 24px;
  display: inline-block;
`;

const Metric = styled(H5)`
  font-size: 40px;
  line-height: 44px;
  display: inline-block;
  + ${Unit} {
    margin: 0 5px;
    &:empty {
      margin: 0;
    }
  }
`;

const MetricWrapper = styled('div')`
  margin: 0 ${cardInnerPadding} ${leading.fourth};
  overflow: hidden;
`;

const Title = styled(H4)`
  font-size: 16px;
  line-height: 20px;
  margin: ${cardInnerPadding} ${cardInnerPadding} ${leading.fourth};
  &:empty {
    display: none;
    & + ${MetricWrapper} {
      margin: ${cardInnerPadding};
    }
  }
`;

const GraphWrapper = styled('div')`
  margin-top: auto;
  position: relative;
  height: 60%;
  svg {
    position: absolute;
    bottom: 0;
    max-height: 100%;
  }
`;
const noDataStyles = css`
  height: 140px;
  padding-top: 12px;
  padding-bottom: 6px;
  border-radius: 12px 16px 12px 2px;
`;

export const CardContainer = styled('div')`
  position: relative;
  background: linear-gradient(
    45deg,
    ${(props) => (props.bgDark ? props.bgDark : colors.grey.altDark)},
    ${(props) => (props.bgLight ? props.bgLight : colors.grey.altDark)}
  );
  border-radius: 12px;
  color: ${colors.white};
  height: 200px;
  display: flex;
  flex-direction: column;
  margin: 0 ${cardGutter};
  transition: all ${animate.transition} linear;

  ${(props) => (props.visibleData ? `` : `${noDataStyles}`)};

  &:first-of-type {
    margin: 0 ${cardGutter} 0 0;
  }

  &:last-of-type {
    margin: 0 0 0 ${cardGutter};
  }

  &:only-child {
    margin: 0;
  }

  &:hover {
    box-shadow: 0 1px 6px 2px ${colors.grey.secondaryDark};
  }
`;

export const CardsRow = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-bottom: 22px;
  ${CardContainer} {
    flex: 1;
  }
`;

export const Card = (props) => {
  const { bgDark, bgLight, data, limit, margin, title, unit, referenceLine, sparkLineStyles } =
    props;

  const visibleData = data && data.length > 1;

  return (
    <CardContainer bgDark={bgDark} bgLight={bgLight} visibleData={visibleData}>
      <Title>{title}</Title>
      {data && (
        <MetricWrapper>
          <Metric>{data[data.length - 1]}</Metric>
          <Unit>{unit}</Unit>
        </MetricWrapper>
      )}

      {data && data.length > 1 ? (
        <GraphWrapper>
          <Sparklines data={data} margin={margin} limit={limit}>
            <SparklinesLine style={sparkLineStyles} />

            {referenceLine ? (
              <SparklinesReferenceLine
                style={{
                  stroke: colors.white,
                  strokeWidth: 0.2,
                  strokeOpacity: 0.8,
                  strokeDasharray: '1, 1',
                }}
              />
            ) : (
              <></>
            )}
          </Sparklines>
        </GraphWrapper>
      ) : null}
    </CardContainer>
  );
};

Card.propTypes = {
  bgDark: PropTypes.string,
  bgLight: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.number),
  limit: PropTypes.number,
  margin: PropTypes.number,
  referenceLine: PropTypes.bool,
  sparkLineStyles: PropTypes.shape({}),
  title: PropTypes.string,
  unit: PropTypes.string,
};

Card.defaultProps = {
  bgDark: null,
  bgLight: null,
  data: null,
  limit: 10,
  margin: 0,
  referenceLine: false,
  sparkLineStyles: { strokeWidth: 1, stroke: colors.white, fill: colors.white },
  title: null,
  unit: null,
};
